import { z } from 'zod';
import { IdSchema, DateTimeSchema, StatusSchema, HttpOrInternalFileUrlSchema } from '../common';
/**
 * User role enum — Gencay Akademi
 *
 * Wire format is lowercase; persistence layer (Prisma) uses uppercase
 * (ADMIN/TEACHER/STUDENT/PARENT). Conversion happens in the user service.
 */
export const UserRoleSchema = z.enum(['admin', 'teacher', 'student', 'parent']);
/**
 * OAuth provider enum
 */
export const OAuthProviderSchema = z.enum(['google', 'apple', 'email']);
/**
 * Base User schema - represents the full user entity
 */
export const UserSchema = z.object({
    id: IdSchema,
    email: z.string().email(),
    name: z.string().min(2).max(100),
    avatar: HttpOrInternalFileUrlSchema.nullable(),
    role: UserRoleSchema.default('student'),
    status: StatusSchema.default('active'),
    emailVerified: z.boolean().default(false),
    oauthProvider: OAuthProviderSchema.nullable(),
    oauthId: z.string().nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    lastLoginAt: DateTimeSchema.nullable(),
});
/**
 * Public user profile (excludes sensitive fields)
 */
export const UserProfileSchema = UserSchema.pick({
    id: true,
    email: true,
    name: true,
    avatar: true,
    role: true,
    emailVerified: true,
    createdAt: true,
});
/**
 * Create user schema (for registration)
 */
export const CreateUserSchema = z.object({
    email: z.string().email('Invalid email format'),
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'Password must contain at least one number'),
    name: z.string().min(2, 'Name must be at least 2 characters').max(100),
});
/**
 * Update user schema (partial update)
 */
export const UpdateUserSchema = z.object({
    name: z.string().min(2).max(100).optional(),
    avatar: HttpOrInternalFileUrlSchema.nullable().optional(),
});
/**
 * Admin update user schema (includes role and status)
 */
export const AdminUpdateUserSchema = UpdateUserSchema.extend({
    role: UserRoleSchema.optional(),
    status: StatusSchema.optional(),
    emailVerified: z.boolean().optional(),
});
/**
 * User query/filter schema
 */
export const UserQuerySchema = z.object({
    search: z.string().optional(),
    role: UserRoleSchema.optional(),
    status: StatusSchema.optional(),
    emailVerified: z.boolean().optional(),
});
//# sourceMappingURL=user.js.map